import { Badge, Divider, Modal, ScrollArea, Stack, Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { AppLanguageMenu, Button, EqualWidthGrid, TextArea } from "@tfg/ui"
import { useState } from "react"

import { Locale } from "@tfg/types/schemas/enum"

import { queryClient } from "~/backend/queryClient"
import { findAllProductPageComponentsQueryKey } from "~/gen/api/hooks/useFindAllProductPageComponents"
import { useUpdateProductPageComponets } from "~/gen/api/hooks/useUpdateProductPageComponets"
import {
  ContentBlock,
  Content,
} from "~/pages/Brand/OrganizationSettings/ProductPages/ProductSheetComponents"
import { useOrganizationSettings } from "~/pages/Brand/OrganizationSettings/store/organization-settings-context"
import { findLanguage, findLanguageFlag } from "~/utils/languages"
import { showAxiosError } from "~/utils/showAxiosError"

import { ProductSheetLanguage, ProductSheetLanguages } from "./constants"

interface Props {
  selectedComponent: {
    productPageComponentId: string
    content: Content
  }
  onClose: () => void
  isOpen: boolean
}

export const EditProductComponentModal = ({
  selectedComponent,
  onClose,
  isOpen,
}: Props) => {
  const [language, setLanguage] = useState<ProductSheetLanguage>(
    ProductSheetLanguages.EN
  )
  const { displaySettingsId, organization } = useOrganizationSettings()
  const { mutate: updateProductPageComponentContent } =
    useUpdateProductPageComponets(selectedComponent.productPageComponentId, {
      mutation: {
        onSuccess: async () => {
          await queryClient.invalidateQueries(
            findAllProductPageComponentsQueryKey({
              displaySettingsId: displaySettingsId ?? "",
            })
          )
          await queryClient.invalidateQueries({
            queryKey: ["organization", organization.id],
          })
          onClose()
          notifications.show({
            title: "Product page component updated",
            message: "The product page component has been updated",
            color: "green",
          })
        },
        onError: (error) => {
          showAxiosError({
            error,
            showNotification: true,
          })
        },
      },
    })

  const [content, setContent] = useState<Content>(() => {
    const firstLanguage = Object.keys(
      selectedComponent.content
    )[0] as ProductSheetLanguage
    const baseBlocks: ContentBlock[] =
      selectedComponent.content[firstLanguage] || []
    return Object.keys(ProductSheetLanguages).reduce((acc, lang) => {
      acc[lang as ProductSheetLanguage] =
        selectedComponent.content[lang as ProductSheetLanguage] ??
        baseBlocks.map((block) => ({
          type: block.type,
          textContent: block.textContent.map(({ text, ...rest }) => ({
            text: "",
            ...rest,
          })),
        }))
      return acc
    }, {} as Content)
  })

  const onSubmit = () => {
    updateProductPageComponentContent({
      productPageComponentId: selectedComponent.productPageComponentId,
      content,
    })
  }

  return (
    <Modal opened={isOpen} onClose={onClose} title="Edit component">
      <form onSubmit={onSubmit}>
        <Stack gap="xs">
          <AppLanguageMenu
            currentLocale={language}
            handleLanguageChange={(value) =>
              setLanguage(value.toLocaleUpperCase() as ProductSheetLanguage)
            }
            languageItems={
              Object.values(ProductSheetLanguages).map((language) => ({
                label: findLanguage(language),
                value: language as Locale,
                icon: findLanguageFlag(language as Locale),
              })) || []
            }
          />
          <Divider />
          <ScrollArea h={300}>
            {content[language]?.map((text, blockIndex) => (
              <Stack key={`${text.type}-${blockIndex}`} gap="xs">
                <Text fw="bold">{text.type}</Text>
                <Stack>
                  {text.textContent.map((textContent, textIndex) => (
                    <TextArea
                      key={`${textContent.type}-${textIndex}`}
                      value={textContent.text}
                      onChange={(e) => {
                        const newContent = structuredClone(content) as Content
                        if (
                          newContent[language]?.[blockIndex]?.textContent?.[
                            textIndex
                          ]
                        ) {
                          newContent[language][blockIndex].textContent[
                            textIndex
                          ].text = e.target.value
                          setContent(newContent)
                        }
                      }}
                      label={<Badge size="xs">{`${textContent.type} `}</Badge>}
                    />
                  ))}
                </Stack>
              </Stack>
            ))}
          </ScrollArea>
          <EqualWidthGrid>
            <Button fullWidth onClick={() => onSubmit()}>
              Save
            </Button>
            <Button fullWidth variant="secondary" onClick={onClose}>
              Cancel
            </Button>
          </EqualWidthGrid>
        </Stack>
      </form>
    </Modal>
  )
}
